import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../../lib/utils';
import { GRID_CONFIG } from '../../constants/tileConfig';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Layers, Search, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ScrollableActions - Horizontal scrollable wrapper with chevron buttons for desktop.
 * On mobile, naturally scrolls via touch.
 */
function ScrollableActions({ children, className }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * 160, behavior: 'smooth' });
  };

  return (
    <div className={cn('relative group', className)}>
      {/* Left chevron - desktop only */}
      {canScrollLeft && (
        <button
          onClick={() => scroll(-1)}
          className="flex absolute left-0 top-0 bottom-0 z-20 items-center px-1 bg-gradient-to-r from-muted/80 to-transparent hover:from-muted to-transparent transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-none"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </div>
      {/* Right chevron - desktop only */}
      {canScrollRight && (
        <button
          onClick={() => scroll(1)}
          className="flex absolute right-0 top-0 bottom-0 z-20 items-center px-1 bg-gradient-to-l from-muted/80 to-transparent hover:from-muted to-transparent transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

/**
 * TileGrid - Three-pane layout container
 * 
 * Desktop: Deck (left) | Search (middle) | Right panel (fixed width)
 * Mobile (<= 768px): Tabbed view — Deck | Search | Stats
 */
function TileGrid({
  layoutMode = 'default',
  onLayoutModeChange,
  deckAreaContent,
  searchPaneContent,
  rightPaneContent,
  children, // utility tiles rendered inside right pane below preview
  className,
  cardViewMode = 'medium',
  isMobile = false,
  t = (key, fallback) => fallback || key,
}) {
  const ratios = GRID_CONFIG.viewModeRatios[cardViewMode] || GRID_CONFIG.viewModeRatios.medium;
  const maxRight = GRID_CONFIG.rightPaneWidth || 320;
  const minRight = GRID_CONFIG.rightPaneMinWidth || 240;

  // Scale right pane between minRight..maxRight across 1100-1400px viewport
  // clamp() lets CSS handle this responsively without JS resize listeners
  const rightWidth = `clamp(${minRight}px, calc(${minRight}px + (100vw - 1100px) * ${(maxRight - minRight) / (1400 - 1100)}), ${maxRight}px)`;

  // Build grid-template-columns: deck fr | search fr | responsive right
  const gridCols = `${ratios.deck}fr ${ratios.search}fr ${rightWidth}`;

  // --- Mobile: Tabbed layout ---
  if (isMobile) {
    return (
      <div className={cn('tile-grid-mobile flex flex-col h-full', className)}>
        <Tabs defaultValue="deck" className="flex flex-col h-full">
          <TabsList className="w-full grid grid-cols-3 flex-shrink-0">
            <TabsTrigger value="deck" className="gap-1.5 text-xs">
              <Layers className="h-3.5 w-3.5" />
              {t('mobile.tabs.deck', 'Deck')}
            </TabsTrigger>
            <TabsTrigger value="search" className="gap-1.5 text-xs">
              <Search className="h-3.5 w-3.5" />
              {t('mobile.tabs.search', 'Search')}
            </TabsTrigger>
            <TabsTrigger value="stats" className="gap-1.5 text-xs">
              <BarChart3 className="h-3.5 w-3.5" />
              {t('mobile.tabs.stats', 'Stats')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="deck" className="flex-1 min-h-0 overflow-hidden mt-2">
            <div className="h-full flex flex-col">
              {deckAreaContent}
            </div>
          </TabsContent>

          <TabsContent value="search" className="flex-1 min-h-0 overflow-hidden mt-2">
            <div className="h-full flex flex-col">
              {searchPaneContent}
            </div>
          </TabsContent>

          <TabsContent value="stats" className="flex-1 min-h-0 overflow-auto mt-2">
            <div className="flex flex-col gap-3">
              {children}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // --- Desktop: Three-pane grid ---
  return (
    <div
      className={cn(
        'tile-grid',
        'grid gap-3 h-full',
        'transition-all duration-300 ease-in-out',
        className
      )}
      style={{
        gridTemplateColumns: gridCols,
      }}
    >
      {/* Left Pane - Deck Area */}
      <div
        className={cn(
          'deck-area',
          'flex flex-col min-h-0 min-w-0',
          'transition-all duration-300 ease-in-out'
        )}
      >
        {deckAreaContent}
      </div>

      {/* Middle Pane - Search */}
      <div
        className={cn(
          'search-pane',
          'flex flex-col min-h-0 min-w-0',
          'transition-all duration-300 ease-in-out'
        )}
      >
        {searchPaneContent}
      </div>

      {/* Right Pane - Preview + Utility Tiles */}
      <div
        className={cn(
          'right-pane',
          'flex flex-col gap-3 min-h-0',
          'transition-all duration-300 ease-in-out'
        )}
      >
        {rightPaneContent}
        {/* Scrollable utility tiles */}
        <div
          className={cn(
            'utility-tiles',
            'flex flex-col gap-3',
            'flex-1 min-h-0 overflow-y-auto'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

TileGrid.propTypes = {
  layoutMode: PropTypes.oneOf(['default', 'expanded']),
  onLayoutModeChange: PropTypes.func,
  deckAreaContent: PropTypes.node,
  searchPaneContent: PropTypes.node,
  rightPaneContent: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  cardViewMode: PropTypes.string,
  isMobile: PropTypes.bool,
  t: PropTypes.func,
};

export { ScrollableActions };
export default TileGrid;
